import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Link from '@material-ui/core/Link';
import { makeStyles } from '@material-ui/core/styles';
import { useForm } from 'react-hook-form';

const useStyles = makeStyles(
  (theme) => ({
    form: {
      padding: '0 2em 2em 2em',
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2.5),
    },
    inputWrapper: {
      borderRadius: '16px',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      backdropFilter: 'blur(10px)',
      backgroundColor: 'rgba(158, 201, 194, 0.15)',
    },
    inputContainer: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
    },
    input: {
      width: '100%',
      backgroundColor: 'transparent',
      border: 'none',
      outline: 'none',
      padding: '16px',
      fontSize: '14px',
      color: theme.palette.text.primary,
      '&::placeholder': {
        color: theme.palette.text.secondary,
      },
    },
    label: {
      fontSize: '14px',
      fontWeight: 500,
      color: theme.palette.text.secondary,
      marginBottom: '8px',
      display: 'block',
    },
    inputGroup: {
      display: 'flex',
      flexDirection: 'column',
    },
    submitButton: {
      width: '100%',
      borderRadius: '16px',
      padding: '16px',
      fontSize: '16px',
      fontWeight: 500,
      textTransform: 'none',
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
      '&:disabled': {
        backgroundColor: theme.palette.action.disabledBackground,
      },
    },
    backToLoginContainer: {
      textAlign: 'center',
      marginTop: theme.spacing(2),
    },
    backToLoginLink: {
      color: '#538181',
      textDecoration: 'none',
      fontSize: '14px',
      '&:hover': {
        textDecoration: 'underline',
      },
    },
    icon: {
      marginRight: theme.spacing(1),
    },
    errorText: {
      color: theme.palette.error.main,
      fontSize: '12px',
      marginTop: '4px',
    },
  }),
  { name: 'ForgotPasswordForm' }
);

const GlassInputWrapper = ({ children, hasError }) => {
  const classes = useStyles();
  return (
    <div className={classes.inputWrapper} style={hasError ? { borderColor: 'rgba(244, 67, 54, 0.7)' } : {}}>
      {children}
    </div>
  );
};

GlassInputWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  hasError: PropTypes.bool,
};

export default ({ onSubmit, submitErrors }) => {
  const classes = useStyles();
  const { register, handleSubmit, setError, errors } = useForm();
  const [loading, setLoading] = useState(false);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (submitErrors && submitErrors.length > 0) {
      submitErrors.forEach((error) => {
        setError(error.param, {
          type: 'manual',
          message: error.msg,
        });
      });
    }
  }, [submitErrors, setError]);

  const handleFormSubmit = async (data) => {
    setLoading(true);
    try {
      await onSubmit(data);
    } catch (error) {
      console.error('Form submit error:', error);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  return (
    <form className={classes.form} onSubmit={handleSubmit(handleFormSubmit)} noValidate>
      <div className={classes.inputGroup}>
        <label className={classes.label} htmlFor="email">
          Email Address
        </label>
        <GlassInputWrapper hasError={!!errors.email}>
          <div className={classes.inputContainer}>
            <input
              id="email"
              name="email"
              type="email"
              placeholder="Enter your email address"
              className={classes.input}
              disabled={loading}
              ref={register({
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
              })}
              autoFocus
            />
          </div>
        </GlassInputWrapper>
        {errors.email && <span className={classes.errorText}>{errors.email.message}</span>}
      </div>

      <Button
        variant="contained"
        type="submit"
        disabled={loading}
        className={classes.submitButton}
        style={{ backgroundColor: 'var(--color-teal)' }}
      >
        {loading && <CircularProgress className={classes.icon} size={18} thickness={2} />}
        Submit
      </Button>

      <div className={classes.backToLoginContainer}>
        <Link href="/#/login" className={classes.backToLoginLink}>
          Back to Login
        </Link>
      </div>
    </form>
  );
};
